import { SearchableSelect } from "@/components/molecules/searchable-select";
import { serviceIcon, useServiceItems } from "./use-service-items";
import { useTranslation } from "@/features/utils/i18n";

interface ServiceSelectProps {
  value: string; // Service ID or empty string for "No service"
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

/** Pick one service, or none. */
export function ServiceSelect({
  value,
  onChange,
  placeholder,
  disabled = false,
}: ServiceSelectProps) {
  const { t } = useTranslation();
  const { items, isLoading } = useServiceItems();

  return (
    <SearchableSelect
      items={items}
      value={value}
      onChange={(v) => onChange(Array.isArray(v) ? v[0] || "" : v)}
      multiple={false}
      placeholder={placeholder ?? t("common.select.service.placeholder")}
      searchPlaceholder={t("common.select.service.search")}
      emptyText={t("common.select.service.empty")}
      isLoading={isLoading}
      disabled={disabled}
      allowNone={true}
      noneOption={{
        id: "",
        label: t("common.select.service.none"),
        description: t("common.select.service.noneDescription"),
        icon: serviceIcon,
      }}
    />
  );
}

interface ServiceMultiSelectProps {
  value: string[]; // Array of service IDs
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  showBadges?: boolean; // Show badges for selected items
}

/**
 * Pick any number of services. Named `ServiceMultiSelect` rather than
 * `ServicesSelect` — the two used to live in sibling folders one letter apart.
 */
export function ServiceMultiSelect({
  value,
  onChange,
  placeholder,
  disabled = false,
  showBadges = true,
}: ServiceMultiSelectProps) {
  const { t } = useTranslation();
  const { items, isLoading } = useServiceItems();

  return (
    <SearchableSelect
      items={items}
      value={value}
      onChange={(v) => onChange(Array.isArray(v) ? v : [v])}
      multiple={true}
      placeholder={
        placeholder ?? t("common.select.service.placeholderMultiple")
      }
      searchPlaceholder={t("common.select.service.search")}
      emptyText={t("common.select.service.empty")}
      isLoading={isLoading}
      disabled={disabled}
      showBadges={showBadges !== false}
      showSelectAll={true}
    />
  );
}
